import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTheme } from "@material-ui/core/styles";
import { Typography, Drawer, Divider, IconButton, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import LeftNavigationPanel from "../../left-navigation-panel/LeftNavigationPanel";
import ProductListContainer from "../ProductListContainer";
import ControlPanel from "../../ControlPanel";
import AppState from "../../../store/AppState";
import {
  selectStoreInfoById,
  selectCurrentStoreId,
  selectCurrencyCode,
  selectCurrentLocale,
} from "../../../store/store-list/StoreListSelectors";
import ItemManagementModal from "../../item-management/ItemManagementModal";
import ColorManagement from "../../color-mangement-modal/ColorManagement";
import FindProductModal from "../../find-products-modal/FindProductsModal";
import ConfirmCategorySelection from "../../left-navigation-panel/ConfirmCategorySelection";
import MultipleStoreLanguageModal from "../../store-language-modal/MultipleStoreLanguageModal";
import FacetsContainer from "../../facets/FacetsContainer";
import FacetsProductListContainer from "../../facets/FacetsProductListContainer";
import ViewContextProvider from "../../../context/view-context";
import EditAttributeValuesModal from "../../facets/EditAttributeValuesModal";
import AddAttributeModal from "../../add-attribute-modal/AddAttributeModal";
import FacetActionConfirmationModal from "../../facets/FacetActionConfirmationModal";
import AddFacetModal from "../../facets/AddFacetModal";
import ItemManagementSFCCModal from "../../item-management-SFCC/ItemManagementSFCCModal";
import { Switch, Route, Redirect, useRouteMatch } from "react-router-dom";
import { CATALOG_NAVIGATION_TABS } from "../../../utils/Constants";
// import PreferencesModal from "../header/PreferencesModal";
import EditFacetBucketModal from "../../facets/EditFacetBucketModal";
import CoreSizesModal from "../../core-sizes-management/CoreSizesModal";
import LogoutConfirmationModal from "../../header/LogoutConfirmationModal";
import AddProductsModal from "../../add-products-modal/AddProductsModal";
import ReplaceProductsModal from "../../replace-product-modal/ReplaceProductsModal";
import RemoveProductsModal from "../../remove-products-modal/RemoveProductsModal";
import { LinearProgressProps } from "@mui/material/LinearProgress";
import {
  selectIsFetchingProductIds,
  selectProgressLoadingPercentage,
  selectCurrentPage,
  selectIsProductListFetching,
} from "../../../store/product-list/ProductListSelectors";
import LeadingImageModal from "../../leading-image-modal/LeadingImageModal";
import VariationManagement from "../../variation-management-modal/VariationManagement";
import { CategoryContextProvider } from "../../../context/CategoryContext";
import {
  selectCallerAccountId,
  selectConfigValue,
} from "../../../store/app-config/AppConfigSelectors";
import {
  loadWatchedProductListAction,
  updateProgressCache,
} from "../../../store/product-list/ProductListActions";
import ProgressBar from "../../common/ProgressBar";
import { useStyles } from "./PersistentDrawerLeftStyles"; // Importing the styles
import {
  defaultDrawerWidth,
  testDrawerWidth,
  minDrawerWidth,
  maxDrawerWidth,
  reducedDrawerWidth,
} from "./constants";
import Icons from "components/common/Icons";
import { addClusterEmbeddingsRequestAction } from "store/ai-cluster/AiClusterAction";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import SubstitutionManagement from "components/substitution-mangement-modal/SubstitutionManagement";
import VariationReassignmentModal from "components/variation-reassignment-modal/VariationReassignmentModal";
import ConfirmRemoveContentSlotModal from "components/ContentSlot/ConfirmRemoveContentSlotModal";
import ConfirmOutOfPositionProducts from "components/left-navigation-panel/ConfirmOutOfPositionProducts";
import FreqBoughtTogether from "components/freq-bought-together-modal/FreqBoughtTogether";
import ConfirmAddContentSlotModal from "components/ContentSlot/ConfirmAddContentSlotModal";
import ProductGroupManagement from "components/product-group-management-modal/ProductGroupManagement";
import AppEnv from "utils/AppEnv";
import LinkedCategoriesModal from "components/linked-categories-modal/LinkedCategoriesModal";
import SyncJobProgressSnackbar from "components/sync-categories/SyncJobProgressSnackbar";
import useSyncJobProgress from "hooks/useSyncJobProgress";
import SyncCategoriesNotificationPanel from "components/sync-categories/SyncCategoriesNotificationPanel";
import SavingSnackbar from "components/SavingSnackbar";
import {
  selectSavingCategoryIds,
} from "store/product-list/ProductListSelectors";
import { selectIsCategoryPreviouslySynced } from "store/sync-categories/SyncCategoriesSelectors";
import ConfirmationBulkLeadingColorAction from "components/ConfirmationBulkLeadingColorAction";
import { useChatbotState } from "hooks/useChatbotState";
import RemainingProductsLoadingModal from "components/RemainingProductsLoadingModal";

export default function PersistentDrawerLeft() {
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState([] as string[]);
  const dispatch = useDispatch();
  const clusterEmbeddingsRequestedRef = useRef<string | null>(null);

  // Sync job progress hook
  const { syncJobDetails, isSnackbarOpen, closeSnackbar } =
    useSyncJobProgress();

  // Save progress indicator - check if any categories are being saved
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  // Show snackbar if any save operation is in progress
  const isSaving =
    savingCategoryIds.length > 0 ;

  const { url } = useRouteMatch();

  const currentStoreId = useSelector(selectCurrentStoreId) ?? "";
  const storeInfo = useSelector((state: AppState) =>
    selectStoreInfoById(state, currentStoreId),
  );

  const [drawerWidth, setDrawerWidth] = React.useState(defaultDrawerWidth);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const originalDrawerWidthRef = React.useRef(defaultDrawerWidth);
  const wasChatbotOpenRef = React.useRef(false);

  const { isChatbotOpen, experiementalChatbotEnabled } = useChatbotState();

  // Adjust drawer width when chatbot opens/closes
  React.useEffect(() => {
    if (experiementalChatbotEnabled) {
      if (isChatbotOpen && !wasChatbotOpenRef.current) {
        originalDrawerWidthRef.current = drawerWidth;
        setDrawerWidth(reducedDrawerWidth);
        wasChatbotOpenRef.current = true;
      } else if (!isChatbotOpen && wasChatbotOpenRef.current) {
        setDrawerWidth(originalDrawerWidthRef.current);
        wasChatbotOpenRef.current = false;
      }
    }
  }, [experiementalChatbotEnabled, isChatbotOpen, drawerWidth]);

  const showItemManagement = "true";
  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isProductListFetching = useSelector(selectIsProductListFetching);
  const pageSize = +(AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24);
  const progressValue = useSelector((state: AppState) =>
    selectProgressLoadingPercentage(state, selectCurrentPage(state), pageSize),
  );
  const currentPage = useSelector(selectCurrentPage);
  const selectedAccount = useSelector(selectCallerAccountId);
  const accountId = useSelector(selectCallerAccountId);
  const categoryId = useSelector(selectCurrentCategory);
  const currencyCode = useSelector(selectCurrencyCode);
  const currentCatalogId = useSelector(selectCurrentCatalogId);
  const currentLocaleCode = useSelector(selectCurrentLocale);
  const isCategoryPreviouslySynced = useSelector(
    selectIsCategoryPreviouslySynced,
  );
  const isBoostBuryEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableBoostBury"),
  );
  const isSubstituionEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableSubstitution"),
  );

  const isAiFeatureEnabled =
    isBoostBuryEnabled === "true" || isSubstituionEnabled === "true";

  function LinearProgressWithLabel(
    props: LinearProgressProps & { value: number },
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <ProgressBar variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2">{`${Math.round(
            props.value,
          )}%`}</Typography>
        </Box>
      </Box>
    );
  }

  const bulkUpdateEnabled =
    useSelector((state: AppState) =>
      selectConfigValue(state, "bulkUpdateEnabled"),
    ) === "true" || showItemManagement === "true";

  const isProductAlertsEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableProductAlerts"),
  );
  const handleDrawerOpen = () => {
    setOpen(true);
    setDrawerWidth(testDrawerWidth);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setDrawerWidth(defaultDrawerWidth);
  };

  const handleMouseMove = (e: any) => {
    const newWidth = e.clientX - document.body.offsetLeft;
    if (newWidth > minDrawerWidth && newWidth < maxDrawerWidth) {
      setDrawerWidth(newWidth);
    }
  };

  const handleMouseDown = (e: any) => {
    e.preventDefault();
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  let priceListId: string = "";
  if (storeInfo) {
    storeInfo.priceLists.forEach((priceList) => {
      if (!priceListId && priceList.typeCode === "OFFER")
        priceListId = priceList.priceListId;
    });
  }

  useEffect(() => {
    if (accountId && isProductAlertsEnabled === "true") {
      dispatch(loadWatchedProductListAction());
    }
  }, [accountId, isProductAlertsEnabled, dispatch]);

  useEffect(
    () => {
      if (
        progressValue === 100 &&
        isAiFeatureEnabled &&
        categoryId &&
        clusterEmbeddingsRequestedRef.current !== categoryId
      ) {
        let embeddingPayload = {
          accountId,
          categoryId,
          catalogId: currentCatalogId,
          localeCode: currentLocaleCode,
          storeId: currentStoreId,
          currencyCode: currencyCode,
          refresh: false,
        };
        dispatch(addClusterEmbeddingsRequestAction(embeddingPayload));
        clusterEmbeddingsRequestedRef.current = categoryId;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [progressValue, isAiFeatureEnabled, categoryId, dispatch],
  );

  useEffect(() => {
    if (progressValue > 0 && progressValue < 100 && categoryId) {
      dispatch(updateProgressCache(categoryId, currentPage, progressValue));
    }
  }, [progressValue, categoryId, currentPage, dispatch]);

  // Auto-open notification panel when a previously synced category is selected
  useEffect(() => {
    if (isCategoryPreviouslySynced && categoryId) {
      setNotificationPanelOpen(true);
    } else {
      setNotificationPanelOpen(false);
    }
  }, [isCategoryPreviouslySynced, categoryId]);

  return (
    <CategoryContextProvider value={selected[0]}>
      {selectedAccount !== "" && (
        <DndProvider backend={HTML5Backend}>
          {isFetchingProductIds && <ProgressBar />}
          {progressValue !== 100 &&
          progressValue !== 0 &&
          isProductListFetching &&
          !isFetchingProductIds ? (
            <LinearProgressWithLabel value={progressValue} />
          ) : null}
          <div className={classes.root}>
            <Drawer
              className={classes.drawer}
              variant="persistent"
              anchor="left"
              open={open}
              PaperProps={{
                style: { position: "relative", width: drawerWidth },
              }}
            >
              <div
                onMouseDown={handleMouseDown}
                className={classes.resizable}
              />

              <div className={classes.drawerHeader}>
                <IconButton
                  onClick={handleDrawerClose}
                  className={classes.drawerHeaderIcon}
                >
                  {theme.direction === "ltr" ? (
                    <Icons
                      classes={classes.arrowIcons}
                      iconId="LeftArrowSmallIcon"
                      type="regular"
                    />
                  ) : (
                    <Icons
                      classes={classes.arrowIcons}
                      iconId="RightArrowSmallIcon"
                      type="regular"
                    />
                  )}
                </IconButton>
              </div>
              <Divider />

              <LeftNavigationPanel
                setSelected={setSelected}
                selected={selected}
              />
            </Drawer>
            <div
              className={clsx(classes.main, {
                [classes.mainShift]: open,
              })}
            >
              <Switch>
                <Route path={`${url}/${CATALOG_NAVIGATION_TABS.PRODUCTS}`}>
                  <main
                    className={clsx(classes.content, {
                      [classes.contentShift]: open,
                    })}
                  >
                    <div className={classes.controlPanel}>
                      <ControlPanel
                        onDrawerOpen={handleDrawerOpen}
                        isOpen={open}
                        categoryId={selected[0]}
                      />
                    </div>
                    <div className={classes.list}>
                      <ProductListContainer />
                    </div>
                  </main>
                </Route>
                <Route path={`${url}/${CATALOG_NAVIGATION_TABS.FACETS}`}>
                  <main
                    className={clsx(classes.content, {
                      [classes.contentShift]: open,
                    })}
                  >
                    <FacetsContainer
                      onDrawerOpen={handleDrawerOpen}
                      isOpen={open}
                    />
                    <div className={classes.list}>
                      <ViewContextProvider>
                        <FacetsProductListContainer />
                      </ViewContextProvider>
                    </div>
                  </main>
                </Route>
                <Redirect to={`${url}/products?accountId=${selectedAccount}`} />
              </Switch>
            </div>
          </div>

          {bulkUpdateEnabled ? (
            <ItemManagementModal />
          ) : (
            <ItemManagementSFCCModal />
          )}
          <VariationReassignmentModal />
          <ColorManagement categoryId={selected[0]} />
          <VariationManagement categoryId={selected[0]} />
          <FindProductModal categoryId={selected[0]} />
          <AddProductsModal />
          <ReplaceProductsModal />
          <RemoveProductsModal />
          <ConfirmCategorySelection setSelected={setSelected} />
          <ConfirmOutOfPositionProducts />
          <MultipleStoreLanguageModal />
          <AddAttributeModal />
          <EditAttributeValuesModal />
          <FacetActionConfirmationModal />
          <AddFacetModal />
          {/* <PreferencesModal /> */}
          <EditFacetBucketModal />
          <CoreSizesModal />
          <LogoutConfirmationModal />
          <LeadingImageModal categoryId={selected[0]} />
          <SubstitutionManagement categoryId={selected[0]} />
          <FreqBoughtTogether categoryId={selected[0]} />
          <ConfirmRemoveContentSlotModal />
          <ConfirmAddContentSlotModal />
          <ProductGroupManagement />
          <LinkedCategoriesModal />
          <ConfirmationBulkLeadingColorAction />
          <RemainingProductsLoadingModal/>

          {/* Sync Job Progress Snackbar */}
          <SyncJobProgressSnackbar
            open={isSnackbarOpen}
            onClose={closeSnackbar}
            jobDetails={syncJobDetails}
          />
          {/* Saving Indicator */}
          <SavingSnackbar open={isSaving} />
          <SyncCategoriesNotificationPanel
            open={notificationPanelOpen}
            onClose={() => setNotificationPanelOpen(false)}
            onToggle={() => setNotificationPanelOpen(!notificationPanelOpen)}
          />
        </DndProvider>
      )}
    </CategoryContextProvider>
  );
}
